import { useMemo } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";

import { useMap } from "../../map/useMapStore";
import { jumpTo } from "../../map/jumpTo";
import {
  betterDirection,
  toLog,
  type Fit,
  type Measure,
  type Point,
} from "../../lib/stats";
import { num } from "../../lib/format";

/**
 * Every zone as one dot, with the fitted line through them.
 *
 * The axes clip to the bulk rather than to the extremes. One arrondissement at
 * €16,000/m² would otherwise set the scale for four hundred communes and leave
 * the shape of the relationship as an unreadable smudge in one corner — so the
 * outliers are counted and reported under the chart instead of being allowed to
 * flatten it.
 */
function robustDomain(values: number[]): [number, number, number] {
  if (values.length === 0) return [0, 1, 0];
  const sorted = [...values].sort((a, b) => a - b);
  const at = (q: number) => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))];
  const lo = at(0.01);
  const hi = at(0.99);
  const pad = (hi - lo) * 0.06 || Math.abs(hi) * 0.1 || 1;
  const min = lo - pad;
  const max = hi + pad;
  const dropped = values.filter((v) => v < min || v > max).length;
  return [min, max, dropped];
}

const GRADIENT_ID = "stats-liveability";

/** Muted enough to sit under the dots without competing with them. */
const GOOD = "#2e7d32";
const BAD = "#cf3a2e";
const WASH = 0.12;

/**
 * Where the plot is a better place to live, as a background wash.
 *
 * The orientation is derived from each measure's `valence`, so it follows the
 * axes rather than being drawn once and hoped over: put the heat island on x
 * and the wash runs green-to-red left-to-right; swap the axis and it flips.
 *
 * When only one axis has a direction the wash is a plain left-right or up-down
 * ramp. When **neither** does there is no wash at all — price against density
 * gets no colouring, because neither of them has a better end and inventing one
 * would be the app smuggling in an opinion.
 *
 * Returned in objectBoundingBox units, where SVG y runs downward — so "good is
 * higher on the chart" means the green stop sits at y = 0.
 */
function gradientVector(
  x: Measure,
  y: Measure,
): { x1: number; y1: number; x2: number; y2: number } | null {
  const gx = x.valence ?? 0;
  const gy = y.valence ?? 0;
  if (gx === 0 && gy === 0) return null;
  return {
    x1: gx === 0 ? 0.5 : gx > 0 ? 0 : 1,
    x2: gx === 0 ? 0.5 : gx > 0 ? 1 : 0,
    y1: gy === 0 ? 0.5 : gy > 0 ? 1 : 0,
    y2: gy === 0 ? 0.5 : gy > 0 ? 0 : 1,
  };
}

const AXIS_LABEL = {
  fontSize: 9,
  fill: "#5a6472",
  letterSpacing: "0.02em",
} as const;

/**
 * The axis caption, with the transform named where one is applied.
 *
 * "log" belongs on the axis rather than in a note underneath: the tick values
 * are printed in the original units, so nothing on the axis itself reveals that
 * the spacing between them is not linear.
 */
function axisCaption(measure: Measure, isLog: boolean): string {
  return isLog ? `${measure.axis} (log)` : measure.axis;
}

export function StatsScatter({
  points,
  xMeasure,
  yMeasure,
  logX,
  logY,
  fit,
}: {
  points: Point[];
  xMeasure: Measure;
  yMeasure: Measure;
  logX: boolean;
  logY: boolean;
  fit: Fit;
}) {
  const map = useMap();

  const data = useMemo(() => {
    const out = [];
    for (const p of points) {
      const x = logX ? toLog(p.x) : p.x;
      const y = logY ? toLog(p.y) : p.y;
      if (x === null || y === null) continue;
      out.push({ ...p, px: x, py: y });
    }
    return out;
  }, [points, logX, logY]);

  const [xMin, xMax, xDropped] = robustDomain(data.map((d) => d.px));
  const [yMin, yMax, yDropped] = robustDomain(data.map((d) => d.py));
  const dropped = xDropped + yDropped;

  // Two points are enough to draw a straight line, and drawing it only across
  // the visible domain stops the fit implying data where there is none.
  const line = useMemo(() => {
    if (fit.slope === null || fit.intercept === null) return [];
    return [xMin, xMax].map((px) => ({ px, ly: fit.intercept! + fit.slope! * px }));
  }, [fit.slope, fit.intercept, xMin, xMax]);

  const tick = (measure: Measure, isLog: boolean) => (v: number) =>
    measure.format(isLog ? 10 ** v : v);

  const vector = gradientVector(xMeasure, yMeasure);

  return (
    <>
      <div className="h-56 w-full">
        <ResponsiveContainer>
          {/*
            Bottom and left margins carry the axis captions. Without them
            recharts draws the label over its own tick numbers.
          */}
          <ComposedChart data={data} margin={{ top: 8, right: 10, bottom: 18, left: 6 }}>
            {vector && (
              <defs>
                <linearGradient id={GRADIENT_ID} {...vector}>
                  <stop offset="0%" stopColor={BAD} stopOpacity={WASH} />
                  {/* A neutral band in the middle, so the wash reads as two
                      ends rather than as a muddy continuum. */}
                  <stop offset="50%" stopColor="#ffffff" stopOpacity={0} />
                  <stop offset="100%" stopColor={GOOD} stopOpacity={WASH} />
                </linearGradient>
              </defs>
            )}
            {/* Drawn before the grid so it sits under everything. */}
            {vector && (
              <ReferenceArea
                x1={xMin}
                x2={xMax}
                y1={yMin}
                y2={yMax}
                fill={`url(#${GRADIENT_ID})`}
                fillOpacity={1}
                stroke="none"
                ifOverflow="hidden"
              />
            )}
            <CartesianGrid stroke="#e3e7ed" strokeDasharray="2 3" />
            <XAxis
              dataKey="px"
              type="number"
              domain={[xMin, xMax]}
              tick={{ fontSize: 9, fill: "#5a6472" }}
              tickFormatter={tick(xMeasure, logX)}
              axisLine={false}
              tickLine={false}
              tickCount={5}
              label={{
                value: axisCaption(xMeasure, logX),
                position: "insideBottom",
                offset: -10,
                style: AXIS_LABEL,
              }}
            />
            <YAxis
              dataKey="py"
              type="number"
              domain={[yMin, yMax]}
              tick={{ fontSize: 9, fill: "#5a6472" }}
              tickFormatter={tick(yMeasure, logY)}
              axisLine={false}
              tickLine={false}
              width={54}
              tickCount={5}
              label={{
                value: axisCaption(yMeasure, logY),
                angle: -90,
                position: "insideLeft",
                offset: 4,
                style: { ...AXIS_LABEL, textAnchor: "middle" },
              }}
            />
            <ZAxis range={[14, 14]} />
            <Tooltip
              cursor={{ stroke: "#5a6472", strokeDasharray: "3 3" }}
              contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e3e7ed" }}
              formatter={(_v, _n, item) => {
                const p = item?.payload;
                if (!p) return ["", ""];
                return [
                  `${xMeasure.format(p.x)} · ${yMeasure.format(p.y)}`,
                  p.name,
                ];
              }}
              labelFormatter={() => ""}
            />
            <Scatter
              data={data}
              fill="#2f6df6"
              fillOpacity={0.42}
              isAnimationActive={false}
              // Recharts types the click payload as its own point shape, so
              // the zone's own fields have to be read back off it.
              onClick={(d) => {
                const p = d as unknown as { lon?: number | null; lat?: number | null };
                if (map && p?.lon != null && p?.lat != null) {
                  jumpTo(map, { lon: p.lon, lat: p.lat, zoom: 13 });
                }
              }}
              style={{ cursor: map ? "pointer" : "default" }}
            />
            {line.length > 0 && (
              <Line
                data={line}
                dataKey="ly"
                stroke="#cf3a2e"
                strokeWidth={1.6}
                dot={false}
                isAnimationActive={false}
                legendType="none"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {/* The axes now name themselves, so this carries only what they cannot. */}
      <p className="mt-1 text-[9px] leading-snug text-ink-soft">
        {num(data.length)} plotted
        {dropped > 0 && ` · ${dropped} outlier${dropped > 1 ? "s" : ""} beyond the axes`}
        {map && " · click a dot to open it on the map"}
      </p>
      <LiveabilityKey x={xMeasure} y={yMeasure} />
    </>
  );
}


/**
 * What the background wash means, and — where there is no wash — why not.
 *
 * The second half matters more than the first. A measure left uncoloured is a
 * deliberate statement that it has no better end, and without saying so the
 * absence of colour reads as an oversight.
 */
function LiveabilityKey({ x, y }: { x: Measure; y: Measure }) {
  const better = [betterDirection(x), betterDirection(y)].filter(
    (v): v is string => v !== null,
  );
  const neutral = [x, y].filter((m) => !m.valence && m.neutralWhy);

  return (
    <div className="mt-1.5 space-y-1">
      {better.length > 0 && (
        <p className="flex items-center gap-1.5 text-[9px] leading-snug text-ink-soft">
          <span
            className="inline-block h-2 w-8 shrink-0 rounded-sm"
            style={{ background: `linear-gradient(to right, ${BAD}33, #ffffff00, ${GOOD}33)` }}
          />
          <span className="min-w-0">
            Better to live in towards the green: {better.join(" and ")}.
          </span>
        </p>
      )}
      {neutral.map((m) => (
        <p key={m.id} className="text-[9px] leading-snug text-ink-soft">
          {m.label} is left uncoloured — {m.neutralWhy}.
        </p>
      ))}
    </div>
  );
}
